import React from 'react';
import { 
  Box, 
  Typography, 
  Stack, 
  Button, 
  IconButton, 
  Card,
  Alert,
  InputAdornment,
  Tooltip,
  Divider
} from '@mui/material';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  ContactEmergency as EmergencyIcon,
  Info as InfoIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import FormTextField from '../../shared/FormTextField';
import StepFormLayout from '../../shared/StepFormLayout';
import FormSection from '../../shared/FormSection';

interface TenantInfoStepProps {
  stepProgress: number;
}

const TenantInfoStep: React.FC<TenantInfoStepProps> = ({ stepProgress }) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tenants',
  });

  // Watch all tenant data for progress calculation
  const tenants = useWatch({ control, name: 'tenants' }) || [];
  
  // Use the progress passed from the main LeaseForm component
  const progress = stepProgress;

  const addTenant = () => {
    append({
      fullName: '',
      email: '',
      phone: '',
      isPrimary: fields.length === 0, // First tenant is primary
      emergencyContact: {
        name: '',
        phone: '',
        relationship: '',
      },
    });
  };

  return (
    <StepFormLayout
      title="Tenant Information"
      description="Details for all tenants signing the lease"
      icon={<GroupsIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
      progress={progress}
    >
      <Box>
      
      {/* Quick tip */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>👥 Tip:</strong> All tenants listed here will be legally responsible for the lease terms. Add everyone who will be living in the property.
        </Typography>
      </Alert>
      
      <Stack spacing={3}>
        {fields.map((field, index) => {
          const tenantProgress = tenants[index] ? 
            [tenants[index]?.fullName, tenants[index]?.email, tenants[index]?.phone, 
             tenants[index]?.emergencyContact?.name, tenants[index]?.emergencyContact?.phone, 
             tenants[index]?.emergencyContact?.relationship]
              .filter(value => value && value.toString().trim() !== '').length / 6 * 100 : 0;
          
          return (
            <FormSection
              key={field.id}
              title={`Tenant ${index + 1}${index === 0 ? ' (Primary)' : ''}`}
              icon={<PersonIcon color="primary" />}
              tooltip={`Contact information and emergency details for tenant ${index + 1}`}
              isRequired={true}
              sx={{
                border: '2px solid',
                borderColor: index === 0 ? 'primary.main' : 'divider',
                position: 'relative'
              }}
            >
              {/* Remove button for additional tenants */}
              {index > 0 && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: 16, 
                  right: 16, 
                  zIndex: 1
                }}>
                  <Tooltip title="Remove this tenant">
                    <IconButton 
                      onClick={() => remove(index)} 
                      sx={{ 
                        color: 'error.main',
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'error.50' },
                        boxShadow: 1
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              
              <Box sx={{ position: 'relative' }}>
                <Stack spacing={3}>
                  {/* Personal Information */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Personal Information
                      </Typography>
                    </Box>
                    
                    <Stack spacing={2}>
                      <FormTextField
                        name={`tenants.${index}.fullName`}
                        control={control}
                        label="Full Legal Name"
                        placeholder="As it appears on ID"
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                        gap: 2 
                      }}>
                        <FormTextField
                          name={`tenants.${index}.email`}
                          control={control}
                          label="Email Address"
                          type="email"
                          placeholder="tenant@example.com"
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <FormTextField
                          name={`tenants.${index}.phone`}
                          control={control}
                          label="Phone Number"
                          type="tel"
                          placeholder="(555) 123-4567"
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>
                  
                  <Divider />
                  
                  {/* Emergency Contact */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <EmergencyIcon color="primary" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Emergency Contact
                      </Typography>
                      <Tooltip title="Required for emergencies and lease notices">
                        <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </Tooltip>
                    </Box>
                    
                    <Stack spacing={2}>
                      <FormTextField
                        name={`tenants.${index}.emergencyContact.name`}
                        control={control}
                        label="Contact Name"
                        placeholder="Full name of emergency contact"
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmergencyIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                        gap: 2 
                      }}>
                        <FormTextField
                          name={`tenants.${index}.emergencyContact.phone`}
                          control={control}
                          label="Contact Phone"
                          type="tel"
                          placeholder="(555) 987-6543"
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <FormTextField
                          name={`tenants.${index}.emergencyContact.relationship`}
                          control={control}
                          label="Relationship"
                          placeholder="Parent, Spouse, Friend, etc."
                          required
                        />
                      </Box>
                    </Stack>
                  </Box>
                  
                  {/* Optional Information */}
                  <FormSection
                    title="Additional Information"
                    icon={<BadgeIcon color="primary" />}
                    tooltip="ID, employer, and previous address details"
                    isRequired={false}
                  >
                      <Stack spacing={2}>
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                          gap: 2 
                        }}>
                          <FormTextField
                            name={`tenants.${index}.driversLicense`}
                            control={control}
                            label="Driver's License"
                            placeholder="DL123456789"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <BadgeIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <FormTextField
                            name={`tenants.${index}.employer`}
                            control={control}
                            label="Employer"
                            placeholder="Company name"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <BusinessIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                          Previous Address
                        </Typography>
                        <FormTextField
                          name={`tenants.${index}.previousAddress.street`}
                          control={control}
                          label="Previous Street Address"
                          placeholder="123 Previous St"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <HomeIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, 
                          gap: 2 
                        }}>
                          <FormTextField
                            name={`tenants.${index}.previousAddress.city`}
                            control={control}
                            label="City"
                            placeholder="Previous City"
                          />
                          <FormTextField
                            name={`tenants.${index}.previousAddress.state`}
                            control={control}
                            label="State"
                            placeholder="CA"
                            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                          />
                          <FormTextField
                            name={`tenants.${index}.previousAddress.zip`}
                            control={control}
                            label="ZIP"
                            placeholder="90210"
                            inputProps={{ maxLength: 10 }}
                          />
                        </Box>
                        <FormTextField
                          name={`tenants.${index}.reasonForMoving`}
                          control={control}
                          label="Reason for Moving"
                          multiline
                          rows={2}
                          placeholder="Brief explanation for leaving previous residence"
                        />
                      </Stack>
                  </FormSection>
                </Stack>
              </Box>
            </FormSection>
          );
        })}
        
        {/* Add Tenant Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addTenant}
            size="large"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                transform: 'translateY(-1px)',
                boxShadow: 2
              }
            }}
          >
            Add Another Tenant
          </Button>
        </Box>
        

        
        {/* Empty state */}
        {fields.length === 0 && (
          <Card 
            elevation={0} 
            sx={{ 
              border: '2px dashed', 
              borderColor: 'grey.300', 
              borderRadius: 2,
              py: 6,
              textAlign: 'center'
            }}
          >
            <GroupsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No tenants added yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add at least one tenant to continue with the lease agreement
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addTenant}
              size="large"
              sx={{ borderRadius: 2 }}
            >
              Add First Tenant
            </Button>
          </Card>
        )}
      </Stack>
      </Box>
    </StepFormLayout>
  );
};

export default TenantInfoStep; 